import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, AlarmClock, ClipboardList, ShieldQuestion, Copy, FileDown, Check, Unlock, Save, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { AiTrustBadge } from "@/components/AiTrustBadge";
import { QuickTriage } from "@/components/QuickTriage";
import { Select } from "@/pages/Projects";
import { RagBadge, RAG_OPTIONS } from "@/components/Rag";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const RAG_FIELDS = [
  ["Overall", "overall_rag"], ["Plan", "plan_rag"], ["Resource", "resource_rag"],
  ["Finance", "finance_rag"], ["Benefits", "benefits_rag"], ["Vendor", "vendor_rag"],
];
const NARRATIVE = [
  ["Exec Summary", "exec_summary", 4],
  ["Progress This Period", "progress_this_period", 5],
  ["Upcoming Focus", "upcoming_focus", 5],
];

const Card = ({ icon: Icon, title, blurb, children, testId }) => (
  <section className="panel p-5" data-testid={testId}>
    <div className="flex items-center gap-2">
      <Icon size={17} style={{ color: "var(--brand-primary)" }} />
      <h3 className="font-display text-base font-semibold text-slate-800">{title}</h3>
    </div>
    <p className="mt-1 text-xs text-slate-500">{blurb}</p>
    <div className="mt-4">{children}</div>
  </section>
);

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch (e) {
    toast.error("Copy was blocked by your browser — select the text and copy manually.");
  }
};

export default function Assistant() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [report, setReport] = useState(null);
  const [edited, setEdited] = useState(null);
  const [busy, setBusy] = useState("");
  const [overdue, setOverdue] = useState(null);

  const loadOverdue = () =>
    api.get("/assistant/overdue").then(({ data }) => setOverdue(data)).catch(() => {});

  useEffect(() => {
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      if (data.length) setProjectId(data[0].id);
    });
    api.get("/assistant/overdue").then(({ data }) => setOverdue(data));
    api.get("/reporting-periods").then(({ data }) => setPeriods(data.filter((p) => p.status !== "Closed"))).catch(() => {});
  }, []);

  const call = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "The Insights Assistant could not complete that request.");
    } finally {
      setBusy("");
    }
  };

  const setReportState = (data) => {
    setReport(data);
    setEdited({
      exec_summary: data.exec_summary || "",
      progress_this_period: data.progress_this_period || "",
      upcoming_focus: data.upcoming_focus || "",
      ...Object.fromEntries(RAG_FIELDS.map(([, k]) => [k, data[k] || "Green"])),
      percent_complete: data.percent_complete ?? 0,
    });
  };

  const generateDraft = () =>
    call("draft", async () => {
      const { data } = await api.post("/assistant/weekly-report", {
        project_id: projectId, reporting_period_id: periodId || null,
      });
      setReportState(data);
      toast.success(data.updated_existing ? "Existing draft refreshed for this period" : "Weekly report draft created");
    });

  const saveDraft = () =>
    call("save", async () => {
      const { data } = await api.put(`/status-updates/${report.id}`, {
        ...edited, percent_complete: Number(edited.percent_complete) || 0,
      });
      setReportState(data);
      toast.success("Draft saved");
    });

  const approve = () =>
    call("approve", async () => {
      await api.put(`/status-updates/${report.id}`, { ...edited, percent_complete: Number(edited.percent_complete) || 0 });
      const { data } = await api.post(`/status-updates/${report.id}/approve`);
      setReportState(data);
      toast.success("Report approved and locked");
    });

  const sendForApproval = () =>
    call("route", async () => {
      await api.put(`/status-updates/${report.id}`, { ...edited, percent_complete: Number(edited.percent_complete) || 0 });
      const { data } = await api.post("/approvals/request", {
        approval_type: "Weekly Report", project_id: projectId, item_id: report.id, level: "Level 1",
      });
      toast.success(`Sent to ${data.routed_to} approver${data.routed_to === 1 ? "" : "s"} for sign-off`);
    });

  const reopen = () =>
    call("reopen", async () => {
      const { data } = await api.post(`/status-updates/${report.id}/reopen`);
      setReportState(data);
      toast.success("Report reopened for editing");
    });

  const downloadPdf = () =>
    call("pdf", async () => {
      const res = await api.get(`/status-updates/${report.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `weekly-report-${report.period_ending || "draft"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });

  const chase = () =>
    call("overdue", async () => {
      const { data } = await api.get("/assistant/overdue?summarise=true");
      setOverdue(data);
      if (data.insight?.error) toast.error(data.insight.error);
    });

  return (
    <Shell
      testId="assistant-page"
      title="Insights Assistant"
      subtitle="Capture, drafting and chase-up support across your portfolio"
      actions={<AiTrustBadge />}
    >
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card
          icon={ClipboardList}
          title="Weekly Report"
          blurb="Drafts this week&rsquo;s report from the project record, RAID log, dependencies and change requests. Edit it, approve it, then download the pack."
          testId="assistant-draft-card"
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <label className="field-label">Project</label>
              <Select testId="assistant-project-select" value={projectId} onChange={setProjectId} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select project" />
            </div>
            <div className="w-56">
              <label className="field-label">Reporting Period</label>
              <Select testId="assistant-period-select" value={periodId} onChange={setPeriodId} options={periods.map((p) => ({ value: p.id, label: `${p.name} (${p.period_ending})` }))} placeholder="Current period" />
            </div>
            <Button data-testid="assistant-draft-btn" disabled={!projectId || busy === "draft"} onClick={generateDraft} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              {busy === "draft" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Generate Draft
            </Button>
          </div>

          {report && edited && (
            <div className="mt-5 space-y-4" data-testid="assistant-draft-result">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-xs text-slate-600">
                  <span
                    data-testid="report-status-pill"
                    className={`mr-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${report.report_status === "Approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                  >
                    {report.report_status || "Draft"}
                  </span>
                  {report.reporting_period_name || `Period ending ${report.period_ending}`}
                  {report.report_status === "Approved" && (
                    <span data-testid="report-approved-meta"> · signed off by {report.approved_by} on {String(report.approved_at || "").slice(0, 10)}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {report.report_status !== "Approved" ? (
                    <>
                      <Button size="sm" variant="outline" data-testid="report-save-btn" disabled={busy === "save"} onClick={saveDraft} className="gap-1.5">
                        {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save Draft
                      </Button>
                      <Button size="sm" variant="outline" data-testid="report-route-btn" disabled={busy === "route"} onClick={sendForApproval} className="gap-1.5">
                        {busy === "route" ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Send for Approval
                      </Button>
                      <Button size="sm" data-testid="report-approve-btn" disabled={busy === "approve"} onClick={approve} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
                        {busy === "approve" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Approve
                      </Button>
                    </>
                  ) : (
                    ["admin", "pmo"].includes(user?.role) && (
                      <Button size="sm" variant="outline" data-testid="report-reopen-btn" disabled={busy === "reopen"} onClick={reopen} className="gap-1.5">
                        {busy === "reopen" ? <Loader2 className="animate-spin" size={13} /> : <Unlock size={13} />} Reopen
                      </Button>
                    )
                  )}
                  <Button size="sm" variant="outline" data-testid="report-pdf-btn" disabled={busy === "pdf"} onClick={downloadPdf} className="gap-1.5">
                    {busy === "pdf" ? <Loader2 className="animate-spin" size={13} /> : <FileDown size={13} />} Download PDF
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {RAG_FIELDS.map(([label, key]) => (
                  <div key={key}>
                    <label className="field-label">{label}</label>
                    {report.report_status === "Approved" ? (
                      <RagBadge value={edited[key]} label={`${label} ${edited[key]}`} testId={`report-rag-${key}`} />
                    ) : (
                      <Select testId={`report-rag-${key}`} value={edited[key]} onChange={(v) => setEdited({ ...edited, [key]: v })} options={RAG_OPTIONS} placeholder="Select" />
                    )}
                  </div>
                ))}
                <div>
                  <label className="field-label">% Complete</label>
                  <Input data-testid="report-percent-input" type="number" min="0" max="100" disabled={report.report_status === "Approved"}
                    value={edited.percent_complete ?? 0} onChange={(e) => setEdited({ ...edited, percent_complete: e.target.value })} />
                </div>
              </div>

              {NARRATIVE.map(([label, key, rows]) => (
                <div key={key}>
                  <div className="flex items-center justify-between">
                    <div className="field-label">{label}</div>
                    <button onClick={() => copy(edited[key] || "")} data-testid={`copy-${key}`} aria-label={`Copy ${label}`} title="Copy" className="text-slate-400 transition-colors hover:text-slate-700">
                      <Copy size={13} />
                    </button>
                  </div>
                  <Textarea
                    data-testid={`draft-${key}`}
                    rows={rows}
                    readOnly={report.report_status === "Approved"}
                    value={edited[key] || ""}
                    onChange={(e) => setEdited({ ...edited, [key]: e.target.value })}
                    className={report.report_status === "Approved" ? "bg-slate-50" : ""}
                  />
                </div>
              ))}
              {report.rationale && <p className="text-xs italic text-slate-500">{report.rationale}</p>}
              <p className="text-[11px] text-slate-400">
                {report.report_status === "Approved"
                  ? "Approved and locked. PMO or an admin can reopen it if something needs changing."
                  : "Saved as a draft against the project's Status Updates. PDFs downloaded before approval are watermarked DRAFT."}
              </p>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card
            icon={ShieldQuestion}
            title="Quick Triage & Create"
            blurb="Paste risks or issues captured in a workshop, site walk or email. They are tidied and graded, then created as real records in the register."
            testId="assistant-triage-card"
          >
            <QuickTriage
              projects={projects}
              projectId={projectId}
              setProjectId={setProjectId}
              onCreated={loadOverdue}
            />
          </Card>

          <Card
            icon={AlarmClock}
            title="Overdue Item Reminders"
            blurb="Risks, issues and dependencies past their due date across every project."
            testId="assistant-overdue-card"
          >
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-slate-600">
                <span data-testid="overdue-count" className="font-display text-2xl font-bold text-slate-900">{overdue?.count ?? "—"}</span>{" "}
                overdue item{overdue?.count === 1 ? "" : "s"} as of {overdue?.as_of || "—"}
              </p>
              <Button variant="outline" data-testid="assistant-overdue-btn" disabled={busy === "overdue"} onClick={chase} className="gap-1.5">
                {busy === "overdue" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Summarise Chase-ups
              </Button>
            </div>

            {overdue?.insight?.summary && (
              <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-4" data-testid="overdue-insight">
                <p className="text-sm leading-relaxed text-slate-700">{overdue.insight.summary}</p>
                {Array.isArray(overdue.insight.priorities) && (
                  <ul className="mt-2 list-inside list-disc text-xs text-slate-600">
                    {overdue.insight.priorities.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                )}
              </div>
            )}

            {overdue && overdue.items.length > 0 ? (
              <div className="mt-4 divide-y divide-slate-100">
                {overdue.items.map((i, idx) => (
                  <div key={idx} data-testid={`overdue-row-${idx}`} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <span className="font-medium text-slate-800">{i.title}</span>
                      <span className="ml-2 text-xs text-slate-400">{i.kind} · {i.project} · {i.owner || "no owner"}</span>
                    </div>
                    <span className="shrink-0 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-red-200">
                      {i.days_overdue}d overdue
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              overdue && <Empty testId="overdue-empty" text="Nothing is overdue. " />
            )}
          </Card>
        </div>
      </div>
    </Shell>
  );
}
